import {useMemo, useState, type CSSProperties} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMedal, faTrophy} from "@fortawesome/free-solid-svg-icons";
import {ExplorerHero, ExplorerHeroStat, ExplorerHeroStats} from "../../../components/explorer-hero";
import {getRandomDashboardHeroImage} from "../../../helpers/dashboardHeroImages";
import {buildAchievementDashboard, formatAchievementDate, formatAchievementValue} from "./helpers";
import AchievementMiniCard from "./achievement-mini-card";
import type {AchievementsViewProps, AchievementTheme} from "./types";
import './achievements.scss';

export default function AchievementsView(props: AchievementsViewProps) {
    const [activeTheme, setActiveTheme] = useState<AchievementTheme | 'all'>('all');
    const heroImage = useMemo(() => getRandomDashboardHeroImage(), []);
    const dashboard = useMemo(() => buildAchievementDashboard(props), [props]);
    const filteredSets = useMemo(() => {
        return activeTheme === 'all'
            ? dashboard.sets
            : dashboard.sets.filter(set => set.theme === activeTheme);
    }, [activeTheme, dashboard.sets]);

    return (
        <div className="discover achievements">
            <ExplorerHero
                backgroundImage={heroImage}
                eyebrow="Achievements"
                title="Achievements"
                intro="Celebrate your birding milestones and keep the next targets in view."
            >
                <ExplorerHeroStats ariaLabel="Achievement summary">
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faMedal} />}
                        label="Total Points"
                        value={dashboard.stats.totalPoints.toLocaleString()}
                    />
                    <ExplorerHeroStat
                        icon={<FontAwesomeIcon icon={faTrophy} />}
                        label="Achievements Completed"
                        value={`${dashboard.stats.earnedAchievements.toLocaleString()} / ${dashboard.stats.totalAchievements.toLocaleString()}`}
                    />
                </ExplorerHeroStats>
            </ExplorerHero>

            <section aria-label="Achievement sets">
                <div className="achievements__theme-filter" aria-label="Achievement theme filters">
                    {dashboard.themeOptions.map(theme => (
                        <button
                            type="button"
                            key={theme.id}
                            className={activeTheme === theme.id ? 'is-active' : ''}
                            onClick={() => setActiveTheme(theme.id)}
                        >
                            {theme.label}
                        </button>
                    ))}
                </div>
                <div className="achievements__sets">
                    {filteredSets.map(set => (
                        <article className="achievement-set-card" key={set.id}>
                            <div className="achievement-hex" style={{'--achievement-color': set.color} as CSSProperties} aria-hidden="true">
                                <FontAwesomeIcon icon={set.icon} />
                            </div>
                            <div className="achievement-set-card__body">
                                <h3>{set.title}</h3>
                                <p>{set.description}</p>
                                <div className="achievement-set-card__meta">
                                    <span>{set.earnedCount.toLocaleString()} / {set.totalCount.toLocaleString()} earned</span>
                                    <span>{set.nextMilestone ? `Next: ${formatAchievementValue(set.nextMilestone.threshold)}` : 'Set complete'}</span>
                                </div>
                                <div
                                    className="achievement-progress"
                                        style={{
                                            '--achievement-progress': `${set.percent}%`,
                                            '--achievement-color': set.color
                                        } as CSSProperties}
                                    >
                                        <span />
                                    </div>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            <section className="achievements__highlights" aria-label="Achievement highlights">
                <div className="achievements__panel">
                    <h2 className="achievements__section-heading">Recently Earned</h2>
                    <div className="achievements__recent-grid">
                        {dashboard.recentEarned.map(achievement => (
                            <AchievementMiniCard
                                key={achievement.id}
                                title={achievement.title}
                                dateLabel={formatAchievementDate(achievement.earnedDate)}
                                color={achievement.color}
                                icon={achievement.icon}
                            />
                        ))}
                    </div>
                </div>

                <div className="achievements__panel">
                    <h2 className="achievements__section-heading">In Progress</h2>
                    <div className="achievements__in-progress">
                        {dashboard.inProgress.map(achievement => (
                            <article className="achievement-progress-card" key={achievement.id}>
                                <div className="achievement-progress-card__badge">
                                    <div className="achievement-hex" style={{'--achievement-color': achievement.color} as CSSProperties} aria-hidden="true">
                                        <FontAwesomeIcon icon={achievement.icon} />
                                    </div>
                                </div>
                                <div>
                                    <span className="achievement-progress-card__kicker">{achievement.setTitle}</span>
                                    <h3>{achievement.title}</h3>
                                    <div className="achievement-progress-card__meta">
                                        <span>{formatAchievementValue(achievement.currentValue)} / {formatAchievementValue(achievement.threshold)}</span>
                                        <span>{achievement.percent}%</span>
                                    </div>
                                    <div
                                        className="achievement-progress"
                                        style={{
                                            '--achievement-progress': `${achievement.percent}%`,
                                            '--achievement-color': achievement.color
                                        } as CSSProperties}
                                    >
                                        <span />
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="achievements__panel" aria-labelledby="achievement-rarity-title">
                <h2 id="achievement-rarity-title" className="achievements__section-heading">Rarity Guide</h2>
                <div className="achievements__rarity">
                    {dashboard.rarityGuide.map(rarity => (
                        <article className="achievement-rarity-card" key={rarity.id}>
                            <div className="achievement-rarity-card__badge">
                                <div className="achievement-hex" style={{'--achievement-color': rarity.color} as CSSProperties} aria-hidden="true">
                                    <FontAwesomeIcon icon={faMedal} />
                                </div>
                            </div>
                            <strong>{rarity.label}</strong>
                            <span>{rarity.description}</span>
                        </article>
                    ))}
                </div>
            </section>
        </div>
    );
}
